// Layout.jsx (UI-only update: move desktop menu buttons into same row as logo + username)
import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import logo from "../assets/logo.png";
import {
    LayoutDashboard,
    Map,
    Wrench,
    Globe,
    Users,
    Megaphone,
    ListChecks,
    Circle,
    Bot,
    ShieldBan,
    ChartLine,
    Building2,
    MessageCircle,
    Workflow,
    Sigma,
    FlaskConical,
    BriefcaseBusiness,
    ChevronDown,
    ExternalLink,
    Tags,
} from "lucide-react";
import { BRANDS, getSelectedBrandCode, setSelectedBrandCode } from "../utils/brandContext";

const Layout = () => {
    const location = useLocation();
    const { logout } = useKindeAuth();
    const [open, setOpen] = useState(false);
    const [brandCode, setBrandCode] = useState(getSelectedBrandCode());

    const [auth, setAuth] = useState(() => ({
        username: localStorage.getItem("username"),
        email: localStorage.getItem("email"),
        role: localStorage.getItem("role"),
    }));

    const syncAuthFromStorage = () => {
        setAuth({
            username: localStorage.getItem("username"),
            email: localStorage.getItem("email"),
            role: localStorage.getItem("role"),
        });
    };

    useEffect(() => {
        syncAuthFromStorage();
        window.addEventListener("authUpdated", syncAuthFromStorage);
        window.addEventListener("storage", syncAuthFromStorage);
        return () => {
            window.removeEventListener("authUpdated", syncAuthFromStorage);
            window.removeEventListener("storage", syncAuthFromStorage);
        };
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);
    useEffect(() => {
        const syncBrand = () => setBrandCode(getSelectedBrandCode());
        window.addEventListener("brandUpdated", syncBrand);
        window.addEventListener("storage", syncBrand);
        return () => {
            window.removeEventListener("brandUpdated", syncBrand);
            window.removeEventListener("storage", syncBrand);
        };
    }, []);

    const onBrandChange = (e) => {
        const code = e.target.value;
        if (code === brandCode) return;
        setSelectedBrandCode(code);
        setBrandCode(code);
        window.location.reload();
    };
    const quickLinks = [
        { label: "Intranet", url: "https://www.ciilock.com/catalogues/intranet/0212intra0au07", icon: Building2 },
        { label: "Chat", url: "https://chat.ciilock.com", icon: MessageCircle },
        { label: "Kaizen", url: "https://kaizen.ciilock.com", icon: Workflow },
        { label: "Euler", url: "https://euler.ciilock.com", icon: Sigma },
        { label: "Lab", url: "https://lab.siigmatic.com.au", icon: FlaskConical },
        { label: "Talent", url: "https://talent.ciilock.com", icon: BriefcaseBusiness },
    ];

    const signOut = () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("username");
        localStorage.removeItem("email");
        localStorage.removeItem("role");
        window.dispatchEvent(new Event("authUpdated"));
        logout();
    };

    const menuItems = useMemo(
        () => [
            { path: "/dashboard", name: "Dashboard" },
            { path: "/map-view", name: "Map" },
            { path: "/enhance-and-expand", name: "Tools" },
            { path: "/domain-management", name: "Domains" },
            { path: "/contact-management", name: "Contacts" },
            { path: "/marketing-list", name: "Marketing" },
            { path: "/queue-management", name: "Queues" },
            { path: "/blacklist", name: "Blacklist" },
            { path: "/controlled-keywords", name: "Keywords" },
            { path: "/link-sessions", name: "Link Sessions" },
            // { path: "/assistant-instruction", name: "AI" },
        ],
        []
    );

    const iconMap = {
        "/dashboard": LayoutDashboard,
        "/map-view": Map,
        "/enhance-and-expand": Wrench,
        "/domain-management": Globe,
        "/contact-management": Users,
        "/marketing-list": Megaphone,
        "/queue-management": ListChecks,
        "/blacklist": ShieldBan,
        "/controlled-keywords": Tags,
        "/link-sessions": ChartLine,
        "/assistant-instruction": Bot,
    };

    // Desktop inline navigation (same row as logo + username)
    const DesktopNavLinks = () => (
        <nav className="hidden md:flex items-center">
            <ul className="flex items-center gap-2">
                {menuItems.map(({ path, name }, i) => {
                    const active = location.pathname === path;
                    const Icon = iconMap[path] || Circle;

                    return (
                        <li key={`${path}-${i}`}>
                            <Link
                                to={path}
                                className={[
                                    "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 border",
                                    active
                                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900",
                                ].join(" ")}
                            >
                                <Icon
                                    className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-400"}`}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{name}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    // Drawer links (mobile)
    const DrawerNavLinks = ({ onNavigate }) => (
        <nav className="w-full">
            <ul className="flex flex-col gap-2 px-3 md:px-4 py-3">
                {menuItems.map(({ path, name }, i) => {
                    const active = location.pathname === path;
                    const Icon = iconMap[path] || Circle;

                    return (
                        <li key={`${path}-${i}`} className="w-full">
                            <Link
                                to={path}
                                onClick={onNavigate}
                                className={[
                                    "flex w-full items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 border",
                                    active
                                        ? "bg-slate-900 text-white border-slate-900 shadow-sm"
                                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900",
                                ].join(" ")}
                            >
                                <Icon
                                    className={`h-4 w-4 shrink-0 ${active ? "text-white" : "text-slate-400"}`}
                                    aria-hidden="true"
                                />
                                <span className="truncate">{name}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );

    const QuickLinksMenu = ({ className = "" }) => {
        const [quickMenuOpen, setQuickMenuOpen] = useState(false);

        useEffect(() => {
            if (!quickMenuOpen) return undefined;

            const handleOutsideClick = (event) => {
                const trigger = event.target.closest("[data-quick-links-root]");
                if (!trigger) setQuickMenuOpen(false);
            };

            window.addEventListener("mousedown", handleOutsideClick);
            return () => window.removeEventListener("mousedown", handleOutsideClick);
        }, [quickMenuOpen]);

        const openLink = (url) => {
            window.open(url, "_blank", "noopener,noreferrer");
            setQuickMenuOpen(false);
        };

        return (
            <div data-quick-links-root className={`relative ${className}`}>
                <button
                    type="button"
                    onClick={() => setQuickMenuOpen((prev) => !prev)}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white">
                        <img src={logo} alt="DataEnhancer logo" className="h-6 w-6 object-contain" />
                    </div>
                    <div className="leading-tight text-left">
                        <div className="text-[15px] font-extrabold tracking-tight text-slate-900">
                            DataEnhancer
                        </div>
                        <div className="text-xs font-medium text-slate-500">
                            Enrichment Platform
                        </div>
                    </div>
                    <ChevronDown
                        className={`h-4 w-4 text-slate-500 transition-transform ${quickMenuOpen ? "rotate-180" : ""}`}
                    />
                </button>

                {quickMenuOpen && (
                    <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-72 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                        <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Click to navigate
                        </div>
                        <ul className="p-2">
                            {quickLinks.map((link) => {
                                const Icon = link.icon;
                                return (
                                    <li key={link.label}>
                                        <button
                                            type="button"
                                            onClick={() => openLink(link.url)}
                                            className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-slate-100"
                                        >
                                            <Icon className="h-4 w-4 text-slate-500" />
                                            <span className="flex-1 text-sm font-semibold text-slate-800">
                                                {link.label}
                                            </span>
                                            <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen w-full bg-gray-100 flex flex-col">
            {/* Top bar (all sizes) */}
            <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-gray-100">
                {/* Single row: Brand + (desktop nav) + user + hamburger (mobile) */}
                <div className="h-16 flex items-center justify-between px-4 gap-4">
                    <QuickLinksMenu className="shrink-0" />

                    {/* Desktop nav sits in the middle and can scroll if needed */}
                    <div className="hidden md:flex flex-1 min-w-0 justify-center">
                        <div className="max-w-full overflow-x-auto no-scrollbar">
                            <DesktopNavLinks />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        {/* brand switcher */}
                        <div className="hidden md:flex items-center">
                            <select
                                id="brand-select-desktop"
                                value={brandCode}
                                onChange={onBrandChange}
                                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            >
                                {BRANDS.map((b) => (
                                    <option key={b.code} value={b.code}>
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <span className="hidden sm:inline text-sm font-medium text-slate-700">
                            {auth.username || "User"}
                        </span>
                        <button
                            onClick={signOut}
                            type="button"
                            className="w-full max-w-[12rem] px-4 py-1 rounded-full bg-red-600 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 ease-in-out"
                        >
                            Sign out
                        </button>

                        {/* Mobile hamburger */}
                        <button
                            aria-label="Open navigation"
                            aria-expanded={open}
                            aria-controls="mobile-drawer"
                            onClick={() => setOpen(true)}
                            className="md:hidden p-2 rounded focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-7 w-7"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer + overlay */}
            <div
                id="mobile-drawer"
                className={`fixed inset-0 z-50 md:hidden ${open ? "" : "pointer-events-none"}`}
            >
                <div
                    className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"
                        }`}
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                />

                <div
                    className={`absolute left-0 top-0 h-full w-[18rem] bg-gray-100 shadow-xl transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"
                        }`}
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="relative h-full flex flex-col">
                        <button
                            aria-label="Close navigation"
                            onClick={() => setOpen(false)}
                            className="absolute right-3 top-3 p-2 rounded focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 text-slate-700"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>

                        <div className="shrink-0 mt-4 ml-4 mr-4">
                            <QuickLinksMenu />
                        </div>

                        <div className="px-4 pt-3">
                            <select
                                id="brand-select-mobile"
                                value={brandCode}
                                onChange={onBrandChange}
                                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            >
                                {BRANDS.map((b) => (
                                    <option key={b.code} value={b.code}>
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            <DrawerNavLinks onNavigate={() => setOpen(false)} />
                        </div>

                        <div className="p-4 border-t border-slate-200">
                            <div className="flex flex-col items-center text-center">
                                <div className="mb-1 font-semibold text-md">
                                    {auth.username || "User"}
                                </div>
                                <div className="mb-3 text-sm text-slate-600 break-all">
                                    {auth.email || "Email"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <main className="flex-1 w-full max-w-full overflow-y-auto">
                <Outlet />
            </main>

            {/* Bottom bar */}
            <footer className="w-full border-t border-slate-200 bg-gray-100">
                <div className="mx-auto w-full max-w-full px-4 py-3 text-xs text-slate-500 flex items-center justify-between">
                    <span>{auth.email || ""}</span>
                    <span className="hidden sm:inline">© DataEnhancer</span>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
